import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'sure-components';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import moment from 'moment-timezone';
import currencyFormatter from 'currency-formatter';
import Button from 'shared/lib/components/Button';
import CardSectionHeading from '../CardSectionHeading';
import CardRowKeyVal from '../CardRowKeyVal';
import PolicyRow from '../PolicyRow';
import VERTICALS from 'shared/lib/constants/verticals';

import './ModalCancelPolicy.css';

const propTypes = {
  title: PropTypes.string,
  afterDismiss: PropTypes.func,
  onDismiss: PropTypes.func,
  className: PropTypes.string,
  policy: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onError: PropTypes.func,
  getPolicyById: PropTypes.func,
  getCancelPolicyCheckout: PropTypes.func,
};

class ModalInfo extends PureComponent {
  state = {
    isCancelingPolicy: false,
    isFetchingCheckout: false,
    cancelPolicyCheckout: {},
  };

  componentDidMount() {
    this.isRefundable && this.fetchCancelPolicyCheckout();
  }

  fetchCancelPolicyCheckout = () => {
    this.setState({
      isFetchingCheckout: true,
    });
    return this.props
      .getCancelPolicyCheckout(this.props.policy.id)
      .then(cancelPolicyCheckout => {
        this.setState({
          isFetchingCheckout: false,
          cancelPolicyCheckout,
        });
      })
      .catch(error => {
        this.setState({
          isFetchingCheckout: false,
        });
        this.props.onError(error);
      });
  };

  handleDismiss = () => {
    this.props.onDismiss();
    if (isFunction(this.props.afterDismiss)) {
      this.props.afterDismiss();
    }
  };

  submitCancelPolicy = () => {
    const { getPolicyById, onCancel } = this.props;

    this.setState({
      isCancelingPolicy: true,
    });

    return onCancel(this.props.policy.id)
      .then(() => getPolicyById(this.props.policy.id))
      .then(this.handleDismiss)
      .catch(error => {
        this.setState({
          isCancelingPolicy: false,
        });
        this.props.onError(error);
      });
  };

  get isRefundable() {
    switch (this.props.policy.vertical_code) {
      case 'landlord':
        return false;
      case 'collectibles':
      default:
        return true;
    }
  }

  get coverageEndDateMoment() {
    switch (this.props.policy.vertical_code) {
      case 'landlord':
        return moment(this.props.policy.billing.next_payment);
      default:
        return moment(this.props.policy.coverage_end_date);
    }
  }

  get infoNode() {
    return (
      <CardRowKeyVal
        title="Coverage ends on"
        detail={this.coverageEndDateMoment.format('MMM D, YYYY')}
      />
    );
  }

  get billingDetailsNode() {
    return [
      <CardSectionHeading key="Billing Details">
        Billing Details
      </CardSectionHeading>,
      flatten(
        map(
          this.state.cancelPolicyCheckout.by_payment_method,
          (paymentMethodDetails, i) => [
            <CardRowKeyVal
              key={`refundTo-${i}`}
              title="Refund to"
              detail={`${
                paymentMethodDetails.payment_method.details.brand
              } •••• ${paymentMethodDetails.payment_method.details.last4}`}
            />,
            <CardRowKeyVal
              key={`refundAmount-${i}`}
              title="Refund amount"
              detail={`${currencyFormatter.format(
                paymentMethodDetails.refund_amount,
                { code: 'USD' }
              )}`}
            />,
          ]
        )
      ),
    ];
  }

  get billingMessageNode() {
    switch (this.props.policy.vertical_code) {
      case 'landlord':
        return (
          <div className="ModalCancelPolicy-info">
            Your credit card will not be billed again.
          </div>
        );
      default:
        return null;
    }
  }

  render() {
    const { title, policy, className } = this.props;
    return (
      !this.state.isFetchingCheckout && (
        <Modal
          title={title || 'Cancel policy'}
          isActive={true}
          onDismiss={this.handleDismiss}
          className={classnames('ModalCancelPolicy', className)}
        >
          <PolicyRow
            policyId={policy.id}
            icon={VERTICALS[policy.vertical_code].icon}
            createdAt={policy.created}
            verticalCode={policy.vertical_code}
            underwriter={policy.underwriter.name}
            details={policy.details}
            status={policy.status}
            withBadge
          />
          {this.infoNode}
          {this.isRefundable && this.billingDetailsNode}
          <div className="ModalCancelPolicy-actions">
            {this.billingMessageNode}
            <Button
              disabled={this.state.isCancelingPolicy}
              isInProgress={this.state.isCancelingPolicy}
              sureStyle="destructive"
              onClick={this.submitCancelPolicy}
            >
              Cancel policy
            </Button>
          </div>
        </Modal>
      )
    );
  }
}

ModalInfo.propTypes = propTypes;

export default ModalInfo;
